/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { mutatorAction } from "satcheljs";

import { CreateActionsBaseParams } from "../CreateActionsBaseParams";
import { GetId } from "../entityMap/GetId";

import { MultiSelectActions } from "./MultiSelectActions";
import { MultiSelectState } from "./MultiSelectState";

/**
 * Creates multi select actions.
 * @param params The parameters.
 * @returns The created actions.
 */
export function createMultiSelectActions<TKey, TEntity, TState extends MultiSelectState<TKey, TEntity>>(
    params: CreateActionsBaseParams<TState>
): MultiSelectActions<TKey, TEntity> {
    const { namespace, store } = params;

    return {
        clearAllSelection: mutatorAction(`${namespace}ClearAllSelection`, () => {
            store().selectedValues.clear();
        }),

        selectValue: mutatorAction(`${namespace}SelectValue`, (entity: TEntity, getId: GetId<TKey, TEntity>) => {
            const key = getId(entity);
            if (!store().selectedValues.has(key)) {
                store().selectedValues.set(key, entity);
            }
        }),

        deselectValue: mutatorAction(`${namespace}DeselectValue`, (entity: TEntity, getId: GetId<TKey, TEntity>) => {
            const key = getId(entity);
            store().selectedValues.delete(key);
        }),

        selectValueByKey: mutatorAction(`${namespace}SelectValueByKey`, (key: TKey, value: TEntity) => {
            store().selectedValues.set(key, value);
        }),

        deselectValueByKey: mutatorAction(`${namespace}DeselectValueByKey`, (key: TKey) => {
            store().selectedValues.delete(key);
        }),

        toggleValue: mutatorAction(`${namespace}ToggleValue`, (entity: TEntity, getId: GetId<TKey, TEntity>) => {
            const key = getId(entity);
            if (store().selectedValues.has(key)) {
                store().selectedValues.delete(key);
            } else {
                store().selectedValues.set(key, entity);
            }
        }),

        isSelected: (key: TKey): boolean => {
            return store().selectedValues.has(key);
        },

        setSelectedValues: mutatorAction(`${namespace}SetSelectedValues`, (entities: TEntity[], getId: GetId<TKey, TEntity>) => {
            store().selectedValues.clear();

            for (const entity of entities) {
                const key = getId(entity);
                store().selectedValues.set(key, entity);
            }
        })
    };
}